import { useInfiniteQuery, useQueries, useQuery } from "@tanstack/react-query";

import { apiGet, ApiError, type QueryParams } from "./client";
import type {
  AckList,
  Batch,
  BatchList,
  ChangeList,
  Outcome,
  CursorList,
  FeedList,
  Load,
  LoadList,
  OutboundList,
  SearchOut,
  VehicleList,
  WhoAmIOut,
} from "./types";

/** Retrying a 404, a 401 or a 400 only delays the answer the operator already has. */
function retry(count: number, error: Error): boolean {
  if (error instanceof ApiError && (error.isNotFound || error.isAuth || error.status === 400))
    return false;
  return count < 2;
}

type Key = string | number;

function useList<T extends { next: Key | null }>(
  path: string,
  params: QueryParams,
  enabled = true,
) {
  return useInfiniteQuery({
    queryKey: [path, params],
    initialPageParam: undefined as Key | undefined,
    queryFn: ({ pageParam }) => apiGet<T>(path, { ...params, next: pageParam }),
    getNextPageParam: (last) => last.next ?? undefined,
    enabled,
    retry,
  });
}

function useRow<T>(path: string, params?: QueryParams) {
  return useQuery({
    queryKey: [path, params],
    queryFn: () => apiGet<T>(path, params),
    retry,
  });
}

function useFind<T>(path: string, q: string) {
  return useQuery({
    queryKey: [path, q],
    queryFn: () => apiGet<T>(path, { q }),
    enabled: q.trim().length > 0,
    retry,
  });
}

/**
 * The Workspace account Cloudflare Access asserts for this session. It changes only when the
 * session does, so it is fetched once and never refetched on focus.
 */
export function useWhoami() {
  return useQuery({
    queryKey: ["/whoami"],
    queryFn: () => apiGet<WhoAmIOut>("/whoami"),
    staleTime: Infinity,
    retry,
  });
}

export function useSearch(q: string, enabled = true) {
  return useQuery({
    queryKey: ["/search", q],
    queryFn: () => apiGet<SearchOut>("/search", { q }),
    enabled: enabled && q.trim().length > 0,
    retry,
  });
}

interface LoadFilters {
  carrier_guid?: string;
  driver_guid?: string;
  status?: string;
  archived?: string;
  deleted?: string;
  limit?: number;
}

export const useLoads = (filters: LoadFilters, enabled = true) =>
  useList<LoadList>("/loads", { ...filters }, enabled);
export const useLoad = (guid: string) => useRow<Load>(`/loads/${guid}`);
export const useLoadSearch = (q: string) => useFind<LoadList>("/loads/search", q);

interface VehicleFilters {
  load_guid?: string;
  limit?: number;
}

export const useVehicles = (filters: VehicleFilters) =>
  useList<VehicleList>("/vehicles", { ...filters });
export const useVehicleSearch = (q: string) => useFind<VehicleList>("/vehicles/search", q);

interface BatchFilters {
  root_guid?: string;
  driver_guid?: string;
  device_guid?: string;
  status?: string;
  window_hours?: number;
  limit?: number;
}

export const useBatches = (filters: BatchFilters) => useList<BatchList>("/batches", { ...filters });
export const useBatch = (guid: string) => useRow<Batch>(`/batches/${guid}`);

interface ChangeFilters {
  root_guid?: string;
  driver_guid?: string;
  batch_guid?: string;
  window_hours?: number;
  limit?: number;
}

export const useChanges = (filters: ChangeFilters) =>
  useList<ChangeList>("/changes", { ...filters });

export type OutcomeCounts = Partial<Record<Outcome, number>>;

export interface BatchOutcome {
  counts: OutcomeCounts | undefined;
  loading: boolean;
  error: Error | null;
}

const BATCH_GUIDS_PER_READ = 50;

async function readOutcomes(batchGuids: string[]): Promise<Map<string, OutcomeCounts>> {
  const counts = new Map<string, OutcomeCounts>();
  let next: number | undefined;
  do {
    const page = await apiGet<ChangeList>("/changes", {
      batch_guid: batchGuids,
      limit: 200,
      next,
    });
    for (const row of page.changes) {
      if (!row.batch_guid) continue;
      const entry = counts.get(row.batch_guid) ?? {};
      entry[row.outcome] = (entry[row.outcome] ?? 0) + 1;
      counts.set(row.batch_guid, entry);
    }
    next = page.next ?? undefined;
  } while (next !== undefined);
  return counts;
}

/**
 * The journal outcomes of each listed batch, counted here: the API serves rows, not
 * aggregates. Batches are read 50 at a time in list order, so a list that grows by "load more"
 * keeps its earlier chunks cached and reads only the new ones.
 */
export function useBatchOutcomes(batchGuids: string[]): (batchGuid: string) => BatchOutcome {
  const guids = [...new Set(batchGuids)];
  const chunks: string[][] = [];
  for (let start = 0; start < guids.length; start += BATCH_GUIDS_PER_READ) {
    chunks.push(guids.slice(start, start + BATCH_GUIDS_PER_READ).sort());
  }
  const results = useQueries({
    queries: chunks.map((chunk) => ({
      queryKey: ["/changes", "outcomes", chunk],
      queryFn: () => readOutcomes(chunk),
      retry,
    })),
  });
  const byGuid = new Map<string, number>();
  chunks.forEach((chunk, index) => chunk.forEach((guid) => byGuid.set(guid, index)));
  return (batchGuid) => {
    const index = byGuid.get(batchGuid);
    const result = index === undefined ? undefined : results[index];
    return {
      counts: result?.data?.get(batchGuid),
      loading: result?.isPending ?? false,
      error: result?.error ?? null,
    };
  };
}

interface OutboundFilters {
  root_guid?: string;
  status?: string;
  window_hours?: number;
  limit?: number;
}

export const useOutbound = (filters: OutboundFilters) =>
  useList<OutboundList>("/outbound", { ...filters });

/** `/feed` is read by a whole connection (driver and carrier together) or by a load alone. */
type FeedFilters = { root_guid?: string; limit?: number } & (
  | { driver_guid: string | null; carrier_guid: string | null }
  | { driver_guid?: undefined; carrier_guid?: undefined }
);

export const useFeed = (filters: FeedFilters) =>
  useList<FeedList>(
    "/feed",
    { ...filters },
    Boolean((filters.driver_guid && filters.carrier_guid) || filters.root_guid),
  );

interface AckFilters {
  driver_guid?: string;
  carrier_guid?: string;
  device_guid?: string;
  root_guid?: string;
  limit?: number;
}

export const useAcks = (filters: AckFilters) => useList<AckList>("/acks", { ...filters });

export const useCursors = (driverGuid: string) =>
  useRow<CursorList>("/cursors", { driver_guid: driverGuid });
